import type { RoutineSchedule } from '../../../common/hooks/use-ardi-api.hook';

type T = (key: string, opts?: any) => string;

/** The browser's timezone, which is the vendor's unless they say otherwise. */
export const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const pad = (n: number) => String(n).padStart(2, '0');

export const timeOf = (s: Pick<RoutineSchedule, 'hour' | 'minute'>) => `${pad(s.hour)}:${pad(s.minute)}`;

/** A weekday's name in the reader's language (0 = Sunday). */
export function weekdayName(day: number, lang: string): string {
  // 2026-09-27 is a Sunday.
  const date = new Date(Date.UTC(2026, 8, 27 + day, 12));
  try {
    return new Intl.DateTimeFormat(lang, { weekday: 'long', timeZone: 'UTC' }).format(date);
  } catch {
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day];
  }
}

/** "Every weekday at 08:00" — in words, in the reader's language. */
export function scheduleSentence(t: T, s: RoutineSchedule, lang: string): string {
  const time = timeOf(s);
  if (s.every === 'day') return t('ARDI.ROUTINES.EVERY_DAY_AT', { time });
  if (s.every === 'weekday') return t('ARDI.ROUTINES.EVERY_WEEKDAY_AT', { time });
  return t('ARDI.ROUTINES.EVERY_WEEK_AT', { day: weekdayName(s.weekday ?? 1, lang), time });
}

/** Time zones the browser knows, the vendor's first. */
export function timezones(current: string): string[] {
  let all: string[] = [];
  try {
    all = (Intl as any).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    all = [];
  }
  const list = all.length ? all : ['UTC', 'Europe/Paris', 'Europe/London', 'Africa/Casablanca', 'America/New_York', 'Asia/Dubai'];
  return [current, ...list.filter((z) => z !== current)];
}
